﻿/**
 * ChatHistoryPanelBody — Workspace Chats (per pageId) or Project Chats
 * (per engagementId). Mirrors the Session 57 utility-panel pattern.
 *
 * Sections: Pinned / Today's Chats / This Week's / Past 30 Days. Each
 * section is collapsible. Pinned items float to the dedicated Pinned section
 * and are protected from bulk delete. Individual delete requires confirmation.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  bulkDeleteChatSessions,
  getUserChatSessions,
  updateChatSessionPin,
  type UserChatSessionDTO,
} from '../../../services/chatHistoryApi';

interface ChatHistoryPanelBodyProps {
  scope: 'workspace' | 'project';
  scopeId: string | null;
}

type QuickFilter = 'all' | 'personal' | 'agents';

interface ChatEntry {
  id: string;
  title: string;
  contextLabel: string;
  whenRelative: string;
  bucket: 'today' | 'week' | 'past30';
  sortOrder: number;
  pinned: boolean;
  pinOrder?: number;
  tag: 'personal' | 'agents';
}
const PIN_ORDER_OFFSET = 1_000_000_000_000_000;

function resolveBucket(date: Date): ChatEntry['bucket'] {
  const now = new Date();
  const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startValue = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.floor((startNow.getTime() - startValue.getTime()) / (1000 * 60 * 60 * 24));

  if (dayDiff <= 0) return 'today';
  if (dayDiff <= 7) return 'week';
  return 'past30';
}

function formatWhenRelative(date: Date, bucket: ChatEntry['bucket']): string {
  if (bucket === 'today') {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
  }
  if (bucket === 'week') {
    return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date);
  }
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
}

function isAgentChatSession(session: UserChatSessionDTO): boolean {
  if ((session.listDataSources?.length ?? 0) > 0) {
    return true;
  }

  const normalizedDataSourceId = (session.DataSourceId ?? '').trim().toLowerCase();
  if (normalizedDataSourceId && normalizedDataSourceId !== '0' && normalizedDataSourceId !== 'null') {
    return true;
  }

  const normalizedLogType = (session.LogType ?? '').trim().toLowerCase();
  return normalizedLogType === 'agent';
}

function toEntry(session: UserChatSessionDTO, scope: 'workspace' | 'project'): ChatEntry {
  const lastDateRaw = session.LastChatDate || session.ModifiedDate || session.CreatedDate || new Date().toISOString();
  const parsed = new Date(lastDateRaw);
  const safeDate = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  const bucket = resolveBucket(safeDate);

  const isAgentChat = isAgentChatSession(session);

  return {
    id: session.SessionId,
    title: session.Name?.trim() || 'Untitled chat',
    contextLabel: scope === 'workspace' ? 'Workspace chat' : 'Project chat',
    whenRelative: formatWhenRelative(safeDate, bucket),
    bucket,
    sortOrder: safeDate.getTime(),
    pinned: session.IsPinned === true,
    pinOrder: undefined,
    tag: isAgentChat ? 'agents' : 'personal',
  };
}

interface SectionProps {
  label: string;
  entries: ChatEntry[];
  initiallyOpen?: boolean;
  openMenuId: string | null;
  deleteConfirmId: string | null;
  onTogglePin: (entryId: string) => void;
  onToggleMenu: (entryId: string) => void;
  onRequestDelete: (entryId: string) => void;
  onConfirmDelete: (entryId: string) => void;
  onCancelDelete: () => void;
  selectedIds: Set<string>;
  onToggleSelection: (entryId: string) => void;
}

const Section: React.FC<SectionProps> = ({
  label,
  entries,
  initiallyOpen = true,
  openMenuId,
  deleteConfirmId,
  onTogglePin,
  onToggleMenu,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  selectedIds,
  onToggleSelection,
}) => {
  const [open, setOpen] = useState(initiallyOpen);

  return (
    <section className={'flyout-section' + (open ? ' flyout-section--open' : '')}>
      <button
        type="button"
        className="flyout-section__header"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="flyout-section__label">{label}</span>
        <span className="material-symbols-outlined flyout-section__chevron" aria-hidden="true">
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>
      {open && (
        entries.length > 0 ? (
          <ul className="flyout-section__cards">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className={`nav-panel__tile${selectedIds.has(entry.id) ? ' nav-panel__tile--selected' : ''}`}
                role="button"
                tabIndex={0}
                aria-selected={selectedIds.has(entry.id)}
                onClick={() => onToggleSelection(entry.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onToggleSelection(entry.id);
                  }
                }}
              >
                <div className="nav-panel__tile-content">
                  <div className="nav-panel__tile-title-row">
                    {entry.pinned && (
                      <span
                        className="material-symbols-outlined nav-panel__pin-indicator"
                        role="img"
                        aria-label="Pinned chat"
                      >
                        push_pin
                      </span>
                    )}
                    <span className="nav-panel__tile-title">{entry.title}</span>
                  </div>
                  <span className="nav-panel__tile-desc">{entry.whenRelative} — {entry.contextLabel}</span>
                </div>
                <div className="nav-panel__tile-actions">
                  <button
                    type="button"
                    className="nav-panel__tile-more"
                    aria-label={`Open actions for ${entry.title}`}
                    aria-expanded={openMenuId === entry.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleMenu(entry.id);
                    }}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">more_vert</span>
                  </button>
                  {openMenuId === entry.id && (
                    <div className="nav-panel__tile-menu" role="menu" aria-label={`${entry.title} actions`}>
                      <button
                        type="button"
                        className="nav-panel__tile-menu-item"
                        onClick={(event) => {
                          event.stopPropagation();
                          onTogglePin(entry.id);
                        }}
                      >
                        <span
                          className={`material-symbols-outlined${entry.pinned ? ' nav-panel__menu-icon--pinned' : ''}`}
                          aria-hidden="true"
                        >
                          {entry.pinned ? 'keep_off' : 'push_pin'}
                        </span>
                        {entry.pinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button type="button" className="nav-panel__tile-menu-item" disabled>
                        <span className="material-symbols-outlined" aria-hidden="true">edit</span>
                        Rename
                      </button>
                      <button type="button" className="nav-panel__tile-menu-item" disabled>
                        <span className="material-symbols-outlined" aria-hidden="true">download</span>
                        Download
                      </button>
                      <button
                        type="button"
                        className="nav-panel__tile-menu-item nav-panel__tile-menu-item--danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          onRequestDelete(entry.id);
                        }}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">delete</span>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                {deleteConfirmId === entry.id && (
                  <div className="nav-panel__delete-confirm">
                    <p className="nav-panel__delete-confirm-text">Delete this chat permanently?</p>
                    <div className="nav-panel__delete-confirm-actions">
                      <button
                        type="button"
                        className="nav-panel__delete-confirm-btn nav-panel__delete-confirm-btn--cancel"
                        onClick={(event) => {
                          event.stopPropagation();
                          onCancelDelete();
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="nav-panel__delete-confirm-btn nav-panel__delete-confirm-btn--confirm"
                        onClick={(event) => {
                          event.stopPropagation();
                          onConfirmDelete(entry.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="flyout-section__empty">No chats in this section.</p>
        )
      )}
    </section>
  );
};

export const ChatHistoryPanelBody = React.memo(function ChatHistoryPanelBody({
  scope,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  scopeId: _scopeId,
}: ChatHistoryPanelBodyProps) {
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [query, setQuery] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const sessions = await getUserChatSessions();
        if (cancelled) return;

        setEntries(
          sessions
            .filter((session) => Boolean(session.SessionId))
            .map((session) => toEntry(session, scope)),
        );
        setStatusMessage(null);
      } catch {
        if (!cancelled) {
          setStatusMessage('Unable to load chat history right now.');
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [scope]);

  // Close filter dropdown when clicking outside it
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  const filteredEntries = useMemo(() => {
    let result = entries;
    const normalized = query.trim().toLowerCase();
    if (normalized) {
      result = result.filter((entry) => {
        const haystack = `${entry.title} ${entry.contextLabel} ${entry.whenRelative}`.toLowerCase();
        return haystack.includes(normalized);
      });
    }
    if (quickFilter !== 'all') {
      result = result.filter((entry) => entry.tag === quickFilter);
    }
    return result;
  }, [entries, query, quickFilter]);

  const orderedEntries = useMemo(
    () => [...filteredEntries].sort((a, b) => b.sortOrder - a.sortOrder),
    [filteredEntries],
  );

  const pinned = orderedEntries
    .filter((entry) => entry.pinned)
    .sort((a, b) => (b.pinOrder ?? b.sortOrder) - (a.pinOrder ?? a.sortOrder));
  const today = orderedEntries.filter((entry) => !entry.pinned && entry.bucket === 'today');
  const week = orderedEntries.filter((entry) => !entry.pinned && entry.bucket === 'week');
  const past30 = orderedEntries.filter((entry) => !entry.pinned && entry.bucket === 'past30');

  const togglePin = (entryId: string) => {
    const entry = entries.find((candidate) => candidate.id === entryId);
    setOpenMenuId(null);

    if (!entry) return;

    void updateChatSessionPin(entryId, !entry.pinned)
      .then((updated) => {
        if (!updated) throw new Error('Chat pin update was not accepted');
        setEntries((prev) =>
          prev.map((candidate) => candidate.id === entryId
            ? { ...candidate, pinned: !candidate.pinned, pinOrder: !candidate.pinned ? PIN_ORDER_OFFSET + Date.now() : undefined }
            : candidate),
        );
      })
      .catch(() => setStatusMessage('Unable to update chat pin. Please try again.'));
  };

  const toggleSelection = (entryId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const handleToggleMenu = (entryId: string) => {
    setDeleteConfirmId(null);
    setOpenMenuId((prev) => (prev === entryId ? null : entryId));
  };

  const requestDelete = (entryId: string) => {
    const entry = entries.find((candidate) => candidate.id === entryId);
    setOpenMenuId(null);

    if (entry?.pinned) {
      setSelectedIds((prev) => {
        if (!prev.has(entryId)) return prev;
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
      setStatusMessage('Pinned chats cannot be deleted and were removed from selection.');
      setDeleteConfirmId(null);
      return;
    }

    setDeleteConfirmId(entryId);
  };

  const confirmDelete = (entryId: string) => {
    const entry = entries.find((candidate) => candidate.id === entryId);
    if (entry?.pinned) {
      setSelectedIds((prev) => {
        if (!prev.has(entryId)) return prev;
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
      setStatusMessage('Pinned chats cannot be deleted and were removed from selection.');
      setDeleteConfirmId(null);
      return;
    }

    setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
    setDeleteConfirmId(null);
  };

  const cancelDelete = () => setDeleteConfirmId(null);

  const confirmBulkDelete = () => {
    const idsToDelete = [...selectedIds].filter((entryId) => entries.some((entry) => entry.id === entryId && !entry.pinned));
    void bulkDeleteChatSessions(idsToDelete)
      .then((result) => {
        const deletedIds = new Set(result.Results.filter((item) => item.IsDeleteSuccessful).map((item) => item.SessionId));
        setEntries((prev) => prev.filter((entry) => !deletedIds.has(entry.id)));
        setSelectedIds((prev) => new Set([...prev].filter((id) => !deletedIds.has(id))));
        setStatusMessage(`Deleted ${deletedIds.size} selected chat${deletedIds.size === 1 ? '' : 's'}.`);
      })
      .catch(() => setStatusMessage('Unable to delete selected chats. Please try again.'))
      .finally(() => setShowBulkConfirm(false));
  };

  const handleBulkDeleteRequest = () => {
    const selectedEntries = entries.filter((entry) => selectedIds.has(entry.id));
    const pinnedSelected = selectedEntries.filter((entry) => entry.pinned);
    const unpinnedSelected = selectedEntries.filter((entry) => !entry.pinned);

    setBulkOpen(false);

    if (pinnedSelected.length > 0) {
      const pinnedIds = new Set(pinnedSelected.map((entry) => entry.id));
      setSelectedIds((prev) => new Set([...prev].filter((id) => !pinnedIds.has(id))));
    }

    if (unpinnedSelected.length === 0) {
      setStatusMessage('Pinned chats cannot be bulk deleted and were removed from selection.');
      return;
    }

    if (pinnedSelected.length > 0) {
      setStatusMessage('Pinned chats were removed from selection. Continue to delete unpinned chats.');
    }

    setShowBulkConfirm(true);
  };

  const sectionProps = {
    openMenuId,
    deleteConfirmId,
    onTogglePin: togglePin,
    onToggleMenu: handleToggleMenu,
    onRequestDelete: requestDelete,
    onConfirmDelete: confirmDelete,
    onCancelDelete: cancelDelete,
    selectedIds,
    onToggleSelection: toggleSelection,
  };

  return (
    <div className="ws-chats-panel__body">
      <div className="ws-chats-panel__actions">
        <div className="ws-chats-panel__actions-top">
          <h3 className="ws-chats-panel__heading">Chat History</h3>
          <div className="ws-chats-panel__actions-right">
            <div className="ws-chats-panel__bulk">
              <button
                type="button"
                className="ws-chats-panel__bulk-btn"
                aria-label="Bulk Actions"
                aria-expanded={bulkOpen}
                onClick={() => setBulkOpen((prev) => !prev)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">more_horiz</span>
                Bulk Actions
                <span className="material-symbols-outlined" aria-hidden="true">
                  {bulkOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>
              {bulkOpen && (
                <div className="ws-chats-panel__bulk-menu" role="menu" aria-label="Bulk actions">
                  <button
                    type="button"
                    className="ws-chats-panel__bulk-menu-item"
                    disabled={selectedIds.size === 0}
                    onClick={handleBulkDeleteRequest}
                  >
                    Delete Selected Chats
                  </button>
                </div>
              )}
            </div>
            <button type="button" className="btn btn--secondary ws-chats-panel__new-btn" aria-label="New chat">
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
            </button>
          </div>
        </div>
        <div className="ws-chats-panel__toolbar">
          <div className="ws-chats-panel__search-wrap">
            <span className="material-symbols-outlined ws-chats-panel__search-icon" aria-hidden="true">
              search
            </span>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              className="ws-chats-panel__search"
            />
          </div>
          <div className="ws-chats-panel__filter-wrap" ref={filterRef}>
            <button
              type="button"
              className="ws-chats-panel__filter-btn"
              aria-label="Filter chats"
              aria-expanded={filterOpen}
              aria-haspopup="listbox"
              onClick={() => setFilterOpen((prev) => !prev)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">filter_list</span>
              {quickFilter === 'all' ? 'All' : quickFilter === 'personal' ? 'Personal' : 'Agents'}
              <span className="material-symbols-outlined ws-chats-panel__filter-chevron" aria-hidden="true">
                {filterOpen ? 'expand_less' : 'expand_more'}
              </span>
            </button>
            {filterOpen && (
              <ul className="ws-chats-panel__filter-dropdown" role="listbox" aria-label="Filter options">
                {(['all', 'personal', 'agents'] as const).map((option) => (
                  <li
                    key={option}
                    role="option"
                    aria-selected={quickFilter === option}
                    className={`ws-chats-panel__filter-option${quickFilter === option ? ' ws-chats-panel__filter-option--active' : ''}`}
                    onClick={() => { setQuickFilter(option); setFilterOpen(false); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setQuickFilter(option);
                        setFilterOpen(false);
                      }
                    }}
                    tabIndex={0}
                  >
                    {option === 'all' ? 'All' : option === 'personal' ? 'Personal' : 'Agents'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {statusMessage && <p className="ws-chats-panel__status">{statusMessage}</p>}
      </div>

      <div className="ws-chats-panel__sections">
        <Section label="Pinned" entries={pinned} {...sectionProps} />
        <Section label="Today's Chats" entries={today} {...sectionProps} />
        <Section label="This Week's" entries={week} {...sectionProps} />
        <Section label="Past 30 Days" entries={past30} initiallyOpen={false} {...sectionProps} />
      </div>

      {filteredEntries.length === 0 && (
        <p className="ws-chats-panel__empty">
          {quickFilter !== 'all'
            ? 'No chats found for this filter.'
            : `No chats available. Start a new ${scope === 'workspace' ? 'workspace' : 'project'} chat.`}
        </p>
      )}

      {showBulkConfirm && (
        <div className="ws-bulk-confirm__overlay" role="dialog" aria-modal="true" aria-label="Confirm bulk delete">
          <div className="ws-bulk-confirm">
            <h4 className="ws-bulk-confirm__title">Delete Selected Chats?</h4>
            <p className="ws-bulk-confirm__body">
              This will delete all unpinned chat sessions in Chat History, including the current conversation if it is unpinned. Are you sure you want to proceed?
            </p>
            <div className="ws-bulk-confirm__actions">
              <button
                type="button"
                className="ws-bulk-confirm__btn ws-bulk-confirm__btn--cancel"
                onClick={() => setShowBulkConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ws-bulk-confirm__btn ws-bulk-confirm__btn--confirm"
                onClick={confirmBulkDelete}
              >
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ChatHistoryPanelBody;
